import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Link2, Unlink, Banknote, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const dt = (v) => (v ? new Date(v).toLocaleString('ko-KR', { hour12: false }) : '-');
const hour = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}시`;
};
const err = (e, f) => toast.error(e?.response?.data?.detail || f);
const inputCls = 'w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm';

const STATUS_LABEL = {
  pending_registration: ['등록 대기', 'bg-amber-100 text-amber-700'],
  connected: ['연결됨', 'bg-emerald-100 text-emerald-700'],
  error: ['오류', 'bg-red-100 text-red-700'],
  stopped: ['해지', 'bg-gray-100 text-gray-500'],
};

const REASON_LABEL = {
  name_mismatch: '입금자명 불일치', multiple_candidates: '동일 금액·이름 주문 여러 건',
  underpaid_or_split: '부족 또는 분할 입금', overpaid: '초과 입금', amount_mismatch: '금액 불일치',
  no_order: '주문 없는 입금', cancelled_order: '취소된 주문 입금', already_paid: '이미 결제완료된 주문',
  already_paid_card: '이미 카드결제된 주문', ref_key_conflict: '거래 식별자 충돌',
  account_inactive: '계좌 연결 오류', outside_window: '입금 허용기간 초과',
  not_auto_eligible: '자동확인 대상 아님', invalid_trans_dt: '거래일시 오류', not_deposit: '출금 거래',
  depositor_ambiguous: '입금자명 불확실', depositor_missing: '입금자명 없음', mock_blocked: 'MOCK 차단', apply_blocked: '반영 차단',
  auto: '자동 매칭', manual_match: '수동 매칭', manual_confirmed_before: '수동확인 주문과 대조', recovered: '복구 처리',
};

// BAROBILL-BANK-REAL-CONNECTION-1 · ① 신청정보 확인 → ② 바로빌 계정 설정 → ③ 가입정보 확인·회원사 등록
// 비밀번호는 등록 요청 1회에만 전송하고 화면 상태에서 즉시 지운다 (서버도 저장하지 않음).
const INFO_FIELDS = [['corp_num', '사업자번호'], ['corp_name', '상호'], ['ceo_name', '대표자명'], ['biz_type', '업태'], ['biz_class', '업종'],
  ['addr1', '사업장 주소'], ['addr2', '상세주소 (선택)'], ['member_name', '담당자 이름'], ['tel', '연락처'], ['hp', '휴대전화 (선택)'], ['email', '이메일']];
const OPTIONAL = ['addr2', 'hp'];
const ID_RE = /^[A-Za-z0-9_]{6,20}$/;

const NewMemberWizard = ({ onDone, prefill }) => {
  const [step, setStep] = useState(1);
  const [f, setF] = useState(() => Object.fromEntries(INFO_FIELDS.map(([k]) => [k, (prefill || {})[k] || ''])));
  const [acct, setAcct] = useState({ member_id: '', member_pwd: '', member_pwd_confirm: '' });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const missing = INFO_FIELDS.filter(([k]) => !OPTIONAL.includes(k) && !String(f[k] || '').trim());
  const acctErr = !ID_RE.test(acct.member_id) ? '아이디는 영문·숫자·밑줄(_) 6~20자' : (acct.member_pwd.length < 6 || acct.member_pwd.length > 20 || /\s/.test(acct.member_pwd)) ? '비밀번호는 공백 없이 6~20자'
    : acct.member_pwd !== acct.member_pwd_confirm ? '비밀번호 확인이 일치하지 않습니다' : acct.member_pwd.toLowerCase() === acct.member_id.toLowerCase() ? '비밀번호는 아이디와 달라야 합니다' : '';
  const check = async () => {
    setBusy(true);
    try { const r = await api.post('/seller/bank/member/preview', { ...f, ...acct }); setPreview(r.data); setStep(3); }
    catch (e) { err(e, '가입정보 확인 실패'); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try { await api.post('/seller/bank/member', { mode: 'new', ...f, ...acct }); setAcct({ member_id: acct.member_id, member_pwd: '', member_pwd_confirm: '' }); toast.success('바로빌 회원사 등록 완료'); onDone(); }
    catch (e) { err(e, '회원사 등록 실패'); if ([400, 409].includes(e?.response?.status)) { setAcct({ ...acct, member_pwd: '', member_pwd_confirm: '' }); setStep(2); } }
    finally { setBusy(false); }
  };
  const nav = 'px-4 py-2 rounded-full text-xs font-bold disabled:opacity-40';
  return (
    <div className="space-y-3" data-testid="bank-member-form">
      <p className="text-[11px] font-bold text-gray-500" data-testid="bank-member-step">{step}/3 · {['신청정보 확인', '바로빌 회원사 계정 설정', '가입정보 확인'][step - 1]}</p>
      {step === 1 && (
        <>
          <p className="text-xs text-gray-500">등록된 판매자 정보를 불러왔습니다. 비어 있는 항목만 입력해주세요. (판매자 정보 자체는 변경되지 않습니다)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {INFO_FIELDS.map(([k, l]) => (
              <label key={k} className="text-[11px] text-gray-500">{l}{!OPTIONAL.includes(k) && !String(f[k] || '').trim() && <span className="text-[#FF3B30]"> · 입력 필요</span>}
                <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} data-testid={`bank-member-${k}`} />
              </label>
            ))}
          </div>
          <button type="button" disabled={missing.length > 0} onClick={() => setStep(2)} className={`${nav} bg-black text-white`} data-testid="bank-member-next">다음: 바로빌 계정 설정</button>
        </>
      )}
      {step === 2 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="text-[11px] text-gray-500">바로빌 아이디<input autoComplete="off" value={acct.member_id} onChange={(e) => setAcct({ ...acct, member_id: e.target.value.trim() })} className={inputCls} data-testid="bank-member-id" /></label>
            <label className="text-[11px] text-gray-500">비밀번호<input type="password" autoComplete="new-password" value={acct.member_pwd} onChange={(e) => setAcct({ ...acct, member_pwd: e.target.value })} className={inputCls} data-testid="bank-member-pwd" /></label>
            <label className="text-[11px] text-gray-500">비밀번호 확인<input type="password" autoComplete="new-password" value={acct.member_pwd_confirm} onChange={(e) => setAcct({ ...acct, member_pwd_confirm: e.target.value })} className={inputCls} data-testid="bank-member-pwd-confirm" /></label>
          </div>
          <p className="text-[11px] text-gray-500">※ 바로빌 회원사 등록에 사용됩니다. 비밀번호는 등록 요청에만 사용되며 LIVEMOA에 저장되지 않습니다. 중복 아이디는 등록 시 안내됩니다.</p>
          {acctErr && (acct.member_id || acct.member_pwd) && <p className="text-[11px] text-[#FF3B30]" data-testid="bank-member-acct-error">{acctErr}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={() => setStep(1)} className={`${nav} border border-black/10 dark:border-white/10`} data-testid="bank-member-prev">이전</button>
            <button type="button" disabled={!!acctErr || busy} onClick={check} className={`${nav} bg-black text-white`} data-testid="bank-member-preview">가입정보 확인</button>
          </div>
        </>
      )}
      {step === 3 && preview && (
        <>
          <div className="rounded-xl bg-black/[0.03] dark:bg-white/5 p-3 text-[11px] grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="bank-member-preview-box">
            {[['사업자번호', 'CorpNum'], ['상호', 'CorpName'], ['대표자', 'CEOName'], ['업태/업종', null], ['주소', 'Addr1'], ['담당자', 'MemberName'], ['연락처', 'TEL'], ['이메일', 'Email'], ['바로빌 아이디', 'ID'], ['비밀번호', 'PWD']].map(([l, k]) => (
              <p key={l}><span className="text-gray-500">{l}</span> <b>{k ? preview.params[k] : `${preview.params.BizType} / ${preview.params.BizClass}`}</b></p>
            ))}
          </div>
          {!preview.regist_enabled && <p className="text-[11px] text-amber-700" data-testid="bank-member-regist-locked">실제 바로빌 회원사 등록은 대표 승인 후 활성화됩니다.</p>}
          <div className="flex gap-2">
            <button type="button" onClick={() => setStep(2)} className={`${nav} border border-black/10 dark:border-white/10`} data-testid="bank-member-back">이전</button>
            <button type="button" disabled={busy || !preview.regist_enabled} onClick={submit} className={`${nav} bg-[#FF3B30] text-white`} data-testid="bank-member-submit">회원사 등록{preview.transport === 'mock' ? ' (MOCK)' : ''}</button>
          </div>
        </>
      )}
    </div>
  );
};

const ExistingMemberForm = ({ onDone, prefill }) => {
  const [f, setF] = useState({ corp_num: (prefill || {}).corp_num || '', member_id: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post('/seller/bank/member', { mode: 'existing', ...f }); toast.success('기존 회원 확인 요청 완료 · 파트너 연결 확인 후 이용됩니다.'); onDone(); }
    catch (e) { err(e, '기존 회원 확인 실패'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="bank-existing-form">
      <p className="text-xs text-gray-500">이미 바로빌에 가입한 사업자는 중복 가입하지 않습니다. 기존 아이디만 입력하며 비밀번호는 받지 않습니다. 엠지컴퍼니 파트너 연결이 확인된 뒤 이용할 수 있습니다.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input placeholder="사업자번호" value={f.corp_num} onChange={(e) => setF({ ...f, corp_num: e.target.value })} className={inputCls} data-testid="bank-existing-corp" />
        <input placeholder="기존 바로빌 아이디" value={f.member_id} onChange={(e) => setF({ ...f, member_id: e.target.value.trim() })} className={inputCls} data-testid="bank-existing-id" />
      </div>
      <button type="button" onClick={submit} disabled={busy || !ID_RE.test(f.member_id)} className="px-4 py-2 rounded-full bg-black text-white text-xs font-bold disabled:opacity-40" data-testid="bank-existing-submit">기존 회원 확인 요청</button>
    </div>
  );
};

const MemberForm = ({ onDone, prefill, member }) => {
  const [mode, setMode] = useState('new');
  if (member?.status === 'existing_member_unlinked') {
    return <p className="text-xs text-amber-700" data-testid="bank-member-existing-pending">기존 바로빌 회원({member.member_id || member.corp_num}) · 엠지컴퍼니 파트너 연결 확인 중입니다. 확인 전에는 계좌 연결·자동확인이 진행되지 않습니다.</p>;
  }
  return (
    <div className="space-y-3">
      <div className="flex gap-1" data-testid="bank-member-mode">
        {[['new', '신규 바로빌 가입'], ['existing', '이미 바로빌 회원']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setMode(k)} className={`px-3 py-1 rounded-full text-[11px] font-bold ${mode === k ? 'bg-black text-white' : 'border border-black/10 dark:border-white/10'}`} data-testid={`bank-member-mode-${k}`}>{l}</button>
        ))}
      </div>
      {mode === 'new' ? <NewMemberWizard onDone={onDone} prefill={prefill} /> : <ExistingMemberForm onDone={onDone} prefill={prefill} />}
    </div>
  );
};

// 전송 코드는 그대로 · 표시용 한글 은행명
const BANK_KO = { KB: '국민은행', SHINHAN: '신한은행', NH: '농협은행', HANA: '하나은행', SC: 'SC제일은행', WOORI: '우리은행',
  IBK: 'IBK기업은행', KDB: 'KDB산업은행', KFCC: '새마을금고', CITI: '한국씨티은행', SUHYUP: '수협은행', CU: '신협',
  EPOST: '우체국', KJBANK: '광주은행', JBBANK: '전북은행', DGB: 'iM뱅크(대구은행)', BUSANBANK: '부산은행', KNBANK: '경남은행',
  EJEJUBANK: '제주은행', KBANK: '케이뱅크' };
const bankLabel = (b) => (BANK_KO[b] ? `${b} (${BANK_KO[b]})` : b);

const AddAccountForm = ({ banks, onDone }) => {
  const [f, setF] = useState({ bank: '', account_number: '', account_holder: '', account_type: 'P' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/seller/bank/accounts', f);
      toast.success('계좌 등록 요청 완료 · 바로빌 공식 화면에서 인증을 완료해주세요.');
      if (r.data?.registration_url) window.open(r.data.registration_url, '_blank', 'noopener');
      else if (r.data?.registration_error) toast.error(`바로빌 계좌등록 화면 발급 실패 (오류코드 ${r.data.registration_error})`);
      onDone(r.data);
    } catch (e) { err(e, '계좌 등록 실패'); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="bank-add-form">
      <select value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })} className={inputCls} data-testid="bank-add-bank">
        <option value="">은행 선택</option>
        {banks.map((b) => <option key={b} value={b}>{bankLabel(b)}</option>)}
      </select>
      <input placeholder="계좌번호 (숫자만)" value={f.account_number} onChange={(e) => setF({ ...f, account_number: e.target.value })} className={inputCls} data-testid="bank-add-number" />
      <input placeholder="예금주" value={f.account_holder} onChange={(e) => setF({ ...f, account_holder: e.target.value })} className={inputCls} data-testid="bank-add-holder" />
      <select value={f.account_type} onChange={(e) => setF({ ...f, account_type: e.target.value })} className={inputCls} data-testid="bank-add-type">
        <option value="P">개인 계좌</option><option value="C">법인 계좌</option>
      </select>
      <p className="px-3 py-2 text-xs font-bold text-gray-600 dark:text-gray-300" data-testid="bank-add-cycle-fixed">계좌조회 주기: 10분 고정</p>
      <button type="button" onClick={submit} disabled={busy} className="px-4 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40" data-testid="bank-add-submit">계좌 연결 요청</button>
      <p className="sm:col-span-2 text-[11px] text-gray-500">계좌 비밀번호·빠른조회 비밀번호는 LIVEMOA에 입력하지 않습니다. 바로빌 등록 화면에서 직접 인증합니다.</p>
      <div className="sm:col-span-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 p-3 text-[11px] text-amber-800 dark:text-amber-300" data-testid="bank-quick-inquiry-notice">
        <p className="font-bold">무통장입금 자동확인을 이용하려면 해당 은행의 빠른조회(간편조회) 서비스를 먼저 등록해야 합니다.</p>
        <p className="mt-1">빠른조회 가입·인증은 각 은행 공식 인터넷뱅킹에서 진행해주세요.{' '}
          <a href="https://ezcare.easyms.co.kr/?PG=bank" target="_blank" rel="noopener noreferrer" className="underline font-bold" data-testid="bank-quick-inquiry-link">은행별 빠른조회 안내 (외부 참고자료)</a>
          {' '}· 은행 공식 서비스가 아닌 참고용 안내입니다.</p>
      </div>
    </div>
  );
};

const AccountRow = ({ a, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (ok) toast.success(typeof ok === 'function' ? ok(r.data) : ok); onChanged(); }
    catch (e) { err(e, '처리 실패'); } finally { setBusy(false); }
  };
  const [label, cls] = STATUS_LABEL[a.status] || [a.status, 'bg-gray-100'];
  const base = `/seller/bank/accounts/${a.account_id}`;
  const disconnect = async () => {
    setBusy(true);
    try { await api.post(`${base}/disconnect`, {}); toast.success('계좌 연결 해제'); onChanged(); }
    catch (e) {
      if (e?.response?.status === 409 && window.confirm(`${e.response.data.detail}\n그래도 해제할까요? (해당 주문은 수동확인으로 처리)`)) {
        try { await api.post(`${base}/disconnect`, { force: true }); toast.success('계좌 연결 해제'); onChanged(); } catch (e2) { err(e2, '해제 실패'); }
      } else if (e?.response?.status !== 409) err(e, '해제 실패');
    } finally { setBusy(false); }
  };
  const btn = 'px-2.5 py-1 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40';
  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 space-y-2" data-testid={`bank-account-${a.account_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-bold">{bankLabel(a.bank)} {a.account_number_masked} · {a.account_holder}</p>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${cls}`} data-testid={`bank-account-status-${a.account_id}`}>{label}</span>
      </div>
      <p className="text-[11px] text-gray-500" data-testid={`bank-account-cycle-${a.account_id}`}>계좌조회 주기: 10분 고정 · 마지막 거래내역 수집: {hour(a.last_polled_at)}
        {a.last_error_code != null && (a.last_error_source === 'mock'
          ? <span className="text-gray-500" data-testid={`bank-account-error-${a.account_id}`}> · MOCK 응답 오류 {a.last_error_code} (실제 계좌 오류 아님)</span>
          : <span className="text-red-600 font-bold" data-testid={`bank-account-error-${a.account_id}`}> · 바로빌 조회 오류 {a.last_error_code}</span>)}</p>
      <p className="text-[11px] text-gray-500" data-testid={`bank-account-source-${a.account_id}`}>계좌 연결상태 확인: {a.status_source === 'live_test' ? '바로빌 TEST 서버' : a.status_source === 'mock' ? 'MOCK (실제 은행 조회 없음)' : '-'} · {hm(a.status_checked_at)}</p>
      {a.last_poll_result && <p className="text-[11px] text-gray-500" data-testid={`bank-account-poll-result-${a.account_id}`}>마지막 조회 결과 ({hm(a.last_poll_attempt_at)}): {a.last_poll_result}</p>}
      {a.status !== 'stopped' && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <label className="inline-flex items-center gap-1 text-xs font-semibold mr-2">
            <input type="checkbox" checked={!!a.auto_confirm} disabled={busy || a.status !== 'connected'} className="accent-[#FF3B30]"
              onChange={(e) => run(() => api.patch(base, { auto_confirm: e.target.checked }), '자동확인 설정 변경')} data-testid={`bank-auto-toggle-${a.account_id}`} />
            자동확인
          </label>
          <button type="button" disabled={busy} className={btn} onClick={() => run(() => api.post(`${base}/sync`), '연결 상태 확인')} data-testid={`bank-sync-${a.account_id}`}><Link2 size={11} /> 상태 확인</button>
          {a.status === 'connected' && (
            <button type="button" disabled={busy} className={btn} data-testid={`bank-poll-${a.account_id}`}
              onClick={() => run(() => api.post(`${base}/poll`, {}), (d) => (d.polled ? `조회 ${d.fetched}건 · 신규 ${d.inserted}건` : `조회 안 함 (${d.reason})`))}>
              <RefreshCw size={11} /> 지금 조회</button>
          )}
          <button type="button" disabled={busy} className={`${btn} text-red-600`} onClick={disconnect} data-testid={`bank-disconnect-${a.account_id}`}><Unlink size={11} /> 연결 해제</button>
        </div>
      )}
    </div>
  );
};

const hm = (v) => {
  if (!v) return '-';
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(v) ? v : `${v}Z`);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const AutoPollStatus = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/seller/bank/auto-poll-status').then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const on = s.enabled && s.accounts.some((a) => a.active);
  return (
    <div className="rounded-xl bg-black/[0.03] dark:bg-white/5 p-2.5 text-[11px] space-y-0.5" data-testid="bank-auto-poll-status">
      <p className="font-bold">자동입금확인 <span className={on ? 'text-emerald-700' : 'text-gray-500'} data-testid="bank-auto-poll-onoff">{on ? 'ON' : 'OFF'}</span>
        {' · '}확인 필요 거래 <span data-testid="bank-auto-poll-review">{s.needs_review}건</span></p>
      <p className="text-gray-500" data-testid="bank-auto-poll-times">LIVEMOA 조회 {s.schedule} · 마지막 조회 {hm(s.last_run?.finished_at)} · 다음 조회 예정 {hm(s.next_poll_at)}</p>
      <p className="text-gray-400">{s.bank_collect} (완료 시각은 늦어질 수 있음)</p>
      {s.accounts.filter((a) => a.reason).map((a) => <p key={a.account_id} className="text-amber-700" data-testid={`bank-auto-poll-reason-${a.account_id}`}>자동조회 대상 아님: {a.reason}</p>)}
    </div>
  );
};

export const BankAccountsPanel = ({ prefill, onChanged }) => {
  const [d, setD] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get('/seller/bank/accounts').then((r) => setD(r.data)).catch((e) => err(e, '계좌 정보를 불러오지 못했습니다.'));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const mock = d.transport === 'mock';
  return (
    <section className="pt-4 border-t border-black/5 dark:border-white/10 space-y-3" data-testid="bank-auto-panel">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-black tracking-tight inline-flex items-center gap-1.5"><Banknote size={14} /> 무통장입금 자동확인 계좌</h3>
        {mock && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-black" data-testid="bank-mock-badge">MOCK · 실제 은행 조회 없음</span>}
      </div>
      {d.legacy && (
        <p className="text-[11px] text-gray-500" data-testid="bank-legacy-note">기존 계좌({d.legacy.bank} {d.legacy.account_number_masked})는 그대로 유지되며, 연결된 계좌가 없을 때 수동 입금확인용으로 안내됩니다.</p>
      )}
      {(!d.member || d.member.status !== 'registered') ? <MemberForm onDone={() => { load(); if (onChanged) onChanged(); }} prefill={prefill} member={d.member} /> : (
        <>
          <p className="text-[11px] text-gray-500" data-testid="bank-member-info">바로빌 회원사 {d.member.corp_name} · {d.member.corp_num}{d.member.member_id && ` · 아이디 ${d.member.member_id}`} · 가입 등록됨 · 파트너 귀속 <b className={d.partner_verified ? 'text-emerald-700' : 'text-amber-700'} data-testid="bank-partner-status">{d.partner_verified ? '확인' : '미확인'}</b></p>
          <p className="text-[11px] text-gray-500" data-testid="bank-cycle-note">은행 거래내역은 10분 주기로 수집됩니다. LIVEMOA는 수집된 내역을 주기적으로 확인하며, 은행 및 시스템 처리 상황에 따라 주문 결제완료 반영이 지연될 수 있습니다.</p>
          <AutoPollStatus key={d.accounts.map((a) => `${a.account_id}${a.auto_confirm}`).join()} />
          <div className="space-y-2">
            {d.accounts.length === 0 && <p className="text-xs text-gray-400" data-testid="bank-no-accounts">연결된 계좌가 없습니다.</p>}
            {d.accounts.map((a) => <AccountRow key={a.account_id} a={a} onChanged={load} />)}
          </div>
          {adding ? <AddAccountForm banks={d.banks} onDone={() => { setAdding(false); load(); }} />
            : <button type="button" onClick={() => setAdding(true)} className="px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold" data-testid="bank-add-open">+ 계좌 추가</button>}
        </>
      )}
    </section>
  );
};

const FEE_LABEL = { unconfirmed: '납부 미확인', needs_check: '납부 확인 필요', confirmed: '납부 확인 완료', suspended: '이용 중지' };

export const BankFeeStep = ({ done, status }) => {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const r = await api.post('/seller/bank/fee-payment-url');
      if (r.data.ready && r.data.url) { window.open(r.data.url, '_blank', 'noopener,noreferrer'); toast.success('바로빌 요금결제 화면을 열었습니다. 납부 확인 후 이용이 활성화됩니다.'); }
      else toast.message(r.data.message || '요금결제 화면을 열 수 없습니다.');
    }
    catch (e) { err(e, '요금결제 화면을 열지 못했습니다.'); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="bank-fee-step">
      <button type="button" disabled={busy} onClick={open} className="px-3 py-1.5 rounded-full bg-black text-white text-xs font-bold disabled:opacity-40" data-testid="bank-fee-open">바로빌 이용료 결제</button>
      <span className={`text-[11px] font-bold ${done ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="bank-fee-status">{FEE_LABEL[status] || (done ? '납부 확인 완료' : '납부 미확인')}{!done && ' · 자동확인 비활성'}</span>
      <span className="text-[11px] text-gray-500">계좌당 월 10,000원 (부가세 별도) · 예정 이용료 · 바로빌에 직접 결제</span>
    </div>
  );
};

const TxRow = ({ t, onChanged }) => {
  const [cands, setCands] = useState(null);
  const [busy, setBusy] = useState(false);
  const review = t.match_status === 'needs_review';
  const openCands = () => api.get(`/seller/bank/transactions/${t.tx_id}/candidates`).then((r) => setCands(r.data)).catch((e) => err(e, '후보 조회 실패'));
  const match = async (soId) => {
    setBusy(true);
    try { await api.post(`/seller/bank/transactions/${t.tx_id}/match`, { seller_order_id: soId }); toast.success('수동 매칭 · 입금확인 완료'); onChanged(); }
    catch (e) { err(e, '매칭 실패'); } finally { setBusy(false); }
  };
  const resolve = async () => {
    const note = window.prompt('처리 메모 (예: 고객에게 환불 완료)');
    if (!note) return;
    try { await api.post(`/seller/bank/transactions/${t.tx_id}/resolve`, { note }); toast.success('확인 완료 처리되었습니다.'); onChanged(); } catch (e) { err(e, '처리 실패'); }
  };
  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 text-xs space-y-1.5" data-testid={`bank-tx-${t.tx_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-bold">{t.depositor_raw || t.remark1 || t.remark2 || '(거래내용 없음)'}</p>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${review ? 'bg-amber-100 text-amber-700' : t.match_status === 'matched' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
          data-testid={`bank-tx-status-${t.tx_id}`}>{t.match_status === 'resolved' ? `처리완료 · ${REASON_LABEL[t.match_reason] || ''}` : (REASON_LABEL[t.match_reason] || t.match_status)}</span>
      </div>
      <div className="grid grid-cols-3 gap-1">
        <span data-testid={`bank-tx-deposit-${t.tx_id}`}>입금 <b className="text-emerald-600">{t.deposit ? won(t.deposit) : '-'}</b></span>
        <span data-testid={`bank-tx-withdraw-${t.tx_id}`}>출금 <b className="text-red-600">{t.withdraw ? won(t.withdraw) : '-'}</b></span>
        <span>잔액 {won(t.balance)}</span>
      </div>
      <p className="text-gray-500">{dt(t.trans_dt)} · {t.account_number_masked} · {t.direction === 'withdraw' ? '출금 (주문 매칭 대상 아님)' : t.matched_seller_order_id ? `주문 ${t.matched_seller_order_id}` : '미매칭'}</p>
      {review && (
        <div className="flex gap-1.5">
          <button type="button" onClick={openCands} className="px-2.5 py-1 rounded-full bg-black text-white text-[11px] font-bold" data-testid={`bank-tx-candidates-${t.tx_id}`}>수동 매칭</button>
          <button type="button" onClick={resolve} className="px-2.5 py-1 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold" data-testid={`bank-tx-resolve-${t.tx_id}`}>확인 완료 처리</button>
        </div>
      )}
      {cands && (
        <div className="space-y-1 pt-1" data-testid={`bank-tx-candidate-list-${t.tx_id}`}>
          {cands.length === 0 && <p className="text-gray-400">결제대기 무통장 주문이 없습니다.</p>}
          {cands.map((c) => (
            <div key={c.seller_order_id} className="flex items-center justify-between gap-2">
              <span>{c.order_number} · {c.depositor_name || '-'} · {won(c.expected_amount)}{!c.amount_match && ' (금액 다름)'}{!c.same_account && ' (다른 계좌)'}</span>
              <button type="button" disabled={busy || !c.amount_match || !c.same_account} onClick={() => match(c.seller_order_id)}
                className="px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold disabled:opacity-30" data-testid={`bank-tx-match-${t.tx_id}-${c.seller_order_id}`}>매칭</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FILTERS = [['', '모든 상태'], ['needs_review', '확인 필요'], ['matched,reconciled', '매칭 완료']];

export const BankDepositsPanel = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [sum, setSum] = useState(null);
  const [filter, setFilter] = useState('');
  const [dir, setDir] = useState('');
  const [txs, setTxs] = useState([]);
  const load = () => {
    api.get('/seller/bank/summary').then((r) => setSum(r.data)).catch(() => {});
    const params = { ...(filter ? { status: filter } : {}), ...(dir ? { direction: dir } : {}) };
    api.get('/seller/bank/transactions', { params }).then((r) => setTxs(r.data)).catch(() => {});
  };
  useEffect(() => { if (open) load(); }, [open, filter, dir]); // eslint-disable-line react-hooks/exhaustive-deps
  const c = sum?.tx_counts || {};
  return (
    <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3" data-testid="bank-deposits-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-sm font-black" data-testid="bank-deposits-toggle">
        <span className="inline-flex items-center gap-1.5"><Banknote size={14} /> 무통장 입금확인</span>
        <span className="text-[11px] font-bold text-gray-500">{open ? '접기' : '열기'}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
            {[['입금확인 완료', sum?.confirmed_orders, CheckCircle2, 'confirmed'], ['결제 대기', sum?.pending_orders, null, 'pending'],
              ['확인 필요', c.needs_review || 0, AlertTriangle, 'review'], ['자동·수동 매칭', (c.matched || 0) + (c.reconciled || 0), null, 'matched']].map(([l, v, I, k]) => (
              <div key={k} className="rounded-xl bg-black/[0.03] dark:bg-white/5 p-2" data-testid={`bank-sum-${k}`}>
                <p className="text-gray-500 inline-flex items-center gap-1">{I && <I size={11} />}{l}</p>
                <p className="text-base font-black">{v ?? 0}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-1" data-testid="bank-direction-filters">
            {[['', '전체'], ['deposit', '입금'], ['withdraw', '출금']].map(([k, l]) => (
              <button key={l} type="button" onClick={() => setDir(k)} className={`px-3 py-1 rounded-full text-[11px] font-bold ${dir === k ? 'bg-[#FF3B30] text-white' : 'border border-black/10 dark:border-white/10'}`} data-testid={`bank-dir-${k || 'all'}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-1">
            {FILTERS.map(([k, l]) => (
              <button key={l} type="button" onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full text-[11px] font-bold ${filter === k ? 'bg-black text-white' : 'border border-black/10 dark:border-white/10'}`} data-testid={`bank-filter-${l}`}>{l}</button>
            ))}
          </div>
          <div className="space-y-2">
            {txs.length === 0 && <p className="text-xs text-gray-400" data-testid="bank-tx-empty">해당 거래가 없습니다.</p>}
            {txs.map((t) => <TxRow key={t.tx_id} t={t} onChanged={() => { load(); if (onChanged) onChanged(); }} />)}
          </div>
        </div>
      )}
    </div>
  );
};
